import { useEffect, useRef, useState } from "react";
import { pollingService } from "@/services/polling/polling-service";

type PollingEventMap = Record<string, (data: unknown) => void>;

export interface UsePollingOptions {
  events: PollingEventMap;
  intervalMs?: number;
  enabled?: boolean;
  /** When true, poll once right after connect. Default false — first poll waits one full interval. */
  pollImmediately?: boolean;
}

export interface UsePollingResult {
  connectionId: string | undefined;
  isConnected: boolean;
}

export const usePolling = ({
  events,
  intervalMs = 2000,
  enabled = true,
  pollImmediately = false,
}: UsePollingOptions): UsePollingResult => {
  const [connectionId, setConnectionId] = useState<string | undefined>();
  const [isConnected, setIsConnected] = useState(false);

  const eventsRef = useRef(events);
  eventsRef.current = events;

  const intervalMsRef = useRef(intervalMs);
  intervalMsRef.current = intervalMs;

  const pollImmediatelyRef = useRef(pollImmediately);
  pollImmediatelyRef.current = pollImmediately;

  const eventNamesKey = Object.keys(events).sort().join("\0");
  const pollInFlightRef = useRef(false);

  useEffect(() => {
    const eventNames = Object.keys(eventsRef.current);

    if (!enabled || eventNames.length === 0) {
      setConnectionId(undefined);
      setIsConnected(false);
      return;
    }

    let cancelled = false;
    let activeConnectionId: string | undefined;
    let sleepTimeout: ReturnType<typeof setTimeout> | null = null;

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        sleepTimeout = setTimeout(() => {
          sleepTimeout = null;
          resolve();
        }, ms);
      });

    const runPoll = async () => {
      if (pollInFlightRef.current || cancelled || !activeConnectionId) {
        return;
      }

      pollInFlightRef.current = true;
      try {
        const { messages } = await pollingService.poll(
          activeConnectionId,
          eventNames
        );
        if (cancelled) return;

        for (const message of messages) {
          const handler = eventsRef.current[message.eventName];
          if (handler) {
            handler(message.data);
          }
        }
      } catch {
        // Best-effort polling; next cycle retries
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const pollLoop = async () => {
      if (pollImmediatelyRef.current) {
        await runPoll();
      }

      while (!cancelled) {
        await wait(intervalMsRef.current);
        if (cancelled) break;
        await runPoll();
      }
    };

    const setup = async () => {
      try {
        const { connectionId: newId } = await pollingService.connect(eventNames);
        if (cancelled) {
          pollingService.disconnect(newId);
          return;
        }

        activeConnectionId = newId;
        setConnectionId(newId);
        setIsConnected(true);
        void pollLoop();
      } catch {
        if (!cancelled) {
          setConnectionId(undefined);
          setIsConnected(false);
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      if (sleepTimeout) {
        clearTimeout(sleepTimeout);
      }
      if (activeConnectionId) {
        pollingService.disconnect(activeConnectionId);
      }
      setConnectionId(undefined);
      setIsConnected(false);
    };
  }, [enabled, eventNamesKey]);

  return { connectionId, isConnected };
};
